import { Button, Select } from "antd";
import React, { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import useItemsGroupContext from "../../../hooks/useItemsGroupContext";
import { ContainerSelectItemsGroup, CustomSelect } from "./styles";

function SelectItemsGroup({
  groupItemId = null,
  customSelect = false,
  placeholder = null,
  value,
  onDeselect,
  onChange,
  disabled = false,
  allowClear = true,
  showSearch = true,
}) {
  const { translation } = useSelector((state) => state.app);
  const {
    options,
    isLoadingOptions,
    fetchOptions,
    openDrawer,
    lastSaved,
    isDrawerVisible,
  } = useItemsGroupContext();

  const awaitingCreate = useRef(false);
  const hasGroupItem = groupItemId != null;

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  useEffect(() => {
    if (awaitingCreate.current && lastSaved?.id) {
      onChange(lastSaved.id);
      awaitingCreate.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSaved]);

  // Declared after the lastSaved effect on purpose: when a save and the drawer
  // close land together, the auto-select must run before the flag is cleared.
  useEffect(() => {
    if (!isDrawerVisible) awaitingCreate.current = false;
  }, [isDrawerVisible]);

  const createNewItemsGroup = useCallback(() => {
    awaitingCreate.current = true;
    openDrawer(null, { groupItemId });
  }, [openDrawer, groupItemId]);

  const filteredItemsGroups = hasGroupItem
    ? options.filter(
        (itemsGroup) =>
          itemsGroup.status !== "Inactive" &&
          itemsGroup.groupItemId === groupItemId,
      )
    : [];

  const optionElements = filteredItemsGroups.map((itemsGroup) => (
    <Select.Option key={itemsGroup.id} value={itemsGroup.id}>
      {itemsGroup.name}
    </Select.Option>
  ));

  const hasMatchingOption = filteredItemsGroups.some(
    (itemsGroup) => itemsGroup.id === value,
  );

  const selectGroupItemFirstMessage =
    translation.itemsGroup.select.selectGroupItemFirst;

  const defaultPlaceholder = placeholder
    ? placeholder
    : translation.defaultSelectPlaceholder;

  const selectProps = {
    loading: isLoadingOptions,
    value: hasMatchingOption ? value : undefined,
    placeholder: hasGroupItem ? defaultPlaceholder : selectGroupItemFirstMessage,
    notFoundContent: hasGroupItem ? undefined : selectGroupItemFirstMessage,
    showSearch,
    optionFilterProp: "children",
    filterOption: (input, option) =>
      option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0,
    allowClear,
    disabled,
    onDeselect,
    onChange,
  };

  const SelectElement = customSelect ? (
    <CustomSelect {...selectProps}>{optionElements}</CustomSelect>
  ) : (
    <Select {...selectProps}>{optionElements}</Select>
  );

  return (
    <ContainerSelectItemsGroup>
      {SelectElement}
      <Button
        shape="circle"
        icon="plus"
        disabled={disabled || !hasGroupItem}
        onClick={createNewItemsGroup}
      />
    </ContainerSelectItemsGroup>
  );
}

export default SelectItemsGroup;
